import { useState, useEffect, useRef, useCallback } from 'react';
import { TelnyxRTC } from '@telnyx/webrtc';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Phone, PhoneOff, PhoneIncoming, Mic, MicOff, Volume2, VolumeX, Loader2, User, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatPhone } from '../utils/formatPhone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InboundCallManager() {
  const [client, setClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callState, setCallState] = useState('idle'); // idle, ringing, active, ended
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callerInfo, setCallerInfo] = useState(null);
  const [audioSettings, setAudioSettings] = useState(null);
  const [error, setError] = useState(null);
  
  const remoteAudioRef = useRef(null);
  const ringtoneRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Fetch audio settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/user/preferences/audio_settings`);
        if (res.data?.value) {
          setAudioSettings(res.data.value);
        }
      } catch (err) {
        // No settings saved, use defaults
      }
    };
    fetchSettings();
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Initialize WebRTC client on mount
  useEffect(() => {
    initClient();
    return () => {
      cleanup();
    };
  }, []);

  // Fallback polling for pending calls when WebRTC isn't connected
  useEffect(() => {
    if (isConnected) return; // WebRTC is handling it
    
    const pollForCalls = async () => {
      try {
        const res = await axios.get(`${API}/telnyx/calls/pending`);
        const pendingCalls = res.data.calls || [];
        
        if (pendingCalls.length > 0 && callState === 'idle') {
          const call = pendingCalls[0];
          setCallerInfo({
            name: call.lead_name || 'Unknown Caller',
            company: call.company_name,
            phone: call.from_number,
            leadId: call.lead_id,
            callId: call.id
          });
          setCallState('ringing');
          playRingtone();
          showBrowserNotification(call.from_number);
        }
      } catch (err) {
        // Ignore polling errors
      }
    };
    
    // Poll every 5 seconds
    const interval = setInterval(pollForCalls, 5000);
    pollForCalls(); // Initial check
    
    return () => clearInterval(interval);
  }, [isConnected, callState]);

  const initClient = async () => {
    try {
      // Check if Telnyx is configured for this user
      const statusRes = await axios.get(`${API}/telnyx/status`);
      if (!statusRes.data?.webrtc_enabled) {
        console.log('WebRTC not configured for this user');
        return;
      }

      // Get WebRTC credentials
      const credRes = await axios.get(`${API}/telnyx/webrtc-credentials`);
      const { username, password } = credRes.data;
      
      if (!username || !password) {
        console.log('WebRTC credentials not available');
        return;
      }

      const telnyxClient = new TelnyxRTC({
        login: username,
        password: password,
        ringtoneFile: null,
        ringbackFile: null,
      });

      telnyxClient.on('telnyx.ready', () => {
        console.log('Inbound call manager: WebRTC ready');
        setIsConnected(true);
        reconnectAttempts.current = 0;
      });

      telnyxClient.on('telnyx.error', (error) => {
        console.error('Inbound call manager error:', error);
        setIsConnected(false);
        attemptReconnect();
      });

      telnyxClient.on('telnyx.socket.close', () => {
        console.log('WebRTC socket closed');
        setIsConnected(false);
        attemptReconnect();
      });

      telnyxClient.on('telnyx.notification', handleNotification);

      await telnyxClient.connect();
      setClient(telnyxClient);
      
    } catch (err) {
      console.error('Failed to init inbound call manager:', err);
      attemptReconnect();
    }
  };

  const attemptReconnect = () => {
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      console.log('Max reconnect attempts reached');
      return;
    }
    
    reconnectAttempts.current++;
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
    console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
    
    setTimeout(() => {
      initClient();
    }, delay);
  };

  const cleanup = () => {
    if (client) {
      try {
        client.disconnect();
      } catch (e) {
        console.log('Cleanup disconnect error:', e);
      }
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
    }
  };

  const handleNotification = useCallback(async (notification) => {
    const call = notification.call;
    if (!call) return;

    console.log('Inbound notification:', notification.type, call.state, call.direction);

    // Handle incoming call invitation
    if (notification.type === 'callUpdate' && call.direction === 'inbound') {
      const state = call.state;
      
      if (state === 'ringing' || state === 'new') {
        // New incoming call
        setIncomingCall(call);
        setCallState('ringing');
        
        // Try to identify caller
        const fromNumber = call.options?.remoteCallerNumber || call.options?.callerNumber || 'Unknown';
        lookupCaller(fromNumber);
        
        // Play ringtone
        playRingtone();
        
        // Show browser notification
        showBrowserNotification(fromNumber);
      } else if (state === 'active') {
        // Call was answered
        setActiveCall(call);
        setCallState('active');
        stopRingtone();
        callStartTimeRef.current = Date.now();
        startDurationTimer();
        
        // Attach audio
        if (call.remoteStream && remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = call.remoteStream;
          remoteAudioRef.current.play().catch(e => console.log('Audio play error:', e));
          
          if (audioSettings?.speaker_id && remoteAudioRef.current.setSinkId) {
            remoteAudioRef.current.setSinkId(audioSettings.speaker_id).catch(e => 
              console.log('Could not set speaker:', e)
            );
          }
        }
        
        toast.success('Call connected');
      } else if (state === 'hangup' || state === 'destroy' || state === 'purge') {
        // Call ended
        stopRingtone();
        stopDurationTimer();
        setCallState('idle');
        setIncomingCall(null);
        setActiveCall(null);
        setCallerInfo(null);
        
        if (callStartTimeRef.current) {
          toast.info(`Call ended - ${formatDuration(callDuration)}`);
        } else {
          toast.info('Call ended');
        }
        callStartTimeRef.current = null;
      }
    }
  }, [audioSettings, callDuration]);

  const lookupCaller = async (phoneNumber) => {
    try {
      // Clean up phone number for search
      const cleanNumber = phoneNumber.replace(/\D/g, '');
      const searchQuery = cleanNumber.slice(-10); // Last 10 digits
      
      // Search for lead by phone number
      const res = await axios.get(`${API}/leads?search=${searchQuery}&limit=1`);
      if (res.data.items?.length > 0) {
        const lead = res.data.items[0];
        setCallerInfo({
          name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown',
          company: lead.company_name,
          phone: phoneNumber,
          leadId: lead.id
        });
      } else {
        setCallerInfo({
          name: 'Unknown Caller',
          phone: phoneNumber
        });
      }
    } catch (err) {
      console.error('Caller lookup failed:', err);
      setCallerInfo({
        name: 'Unknown Caller',
        phone: phoneNumber
      });
    }
  };

  const playRingtone = () => {
    try {
      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio('/ringtone.mp3');
        ringtoneRef.current.loop = true;
      }
      ringtoneRef.current.play().catch(e => console.log('Ringtone play error:', e));
    } catch (e) {
      console.log('No ringtone available');
    }
  };

  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  };

  const showBrowserNotification = (phoneNumber) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Incoming Call', {
        body: `Call from ${phoneNumber}`,
        icon: '/logo.png',
        requireInteraction: true,
        tag: 'incoming-call'
      });
    }
  };

  const answerCall = async () => {
    if (!incomingCall) return;
    
    try {
      // Configure audio
      const audioConstraints = audioSettings?.microphone_id 
        ? { deviceId: { exact: audioSettings.microphone_id } }
        : true;
      
      await incomingCall.answer({ 
        audio: audioConstraints,
        video: false 
      });
      
      setActiveCall(incomingCall);
      setIncomingCall(null);
      stopRingtone();
      
    } catch (err) {
      console.error('Failed to answer call:', err);
      toast.error('Failed to answer call');
    }
  };

  const declineCall = () => {
    if (incomingCall) {
      try {
        incomingCall.hangup();
      } catch (e) {
        console.log('Decline error:', e);
      }
    }
    stopRingtone();
    setIncomingCall(null);
    setCallState('idle');
    setCallerInfo(null);
  };

  const hangUp = () => {
    if (activeCall) {
      try {
        activeCall.hangup();
      } catch (e) {
        console.log('Hangup error:', e);
      }
    }
    stopDurationTimer();
    setActiveCall(null);
    setCallState('idle');
    setCallerInfo(null);
  };

  const toggleMute = () => {
    if (activeCall) {
      if (isMuted) {
        activeCall.unmuteAudio();
      } else {
        activeCall.muteAudio();
      }
      setIsMuted(!isMuted);
    }
  };

  const toggleDeafen = () => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = !isDeafened;
      setIsDeafened(!isDeafened);
    }
  };

  const startDurationTimer = () => {
    durationIntervalRef.current = setInterval(() => {
      if (callStartTimeRef.current) {
        const elapsed = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
        setCallDuration(elapsed);
      }
    }, 1000);
  };

  const stopDurationTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    setCallDuration(0);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Don't render anything if no incoming/active call
  if (callState === 'idle') {
    return (
      <>
        {/* Hidden audio element for call audio */}
        <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
      </>
    );
  }

  return (
    <>
      {/* Hidden audio element */}
      <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
      
      {/* Incoming Call Notification - Fixed position at top right */}
      {callState === 'ringing' && incomingCall && (
        <div 
          className="fixed top-4 right-4 z-[100] animate-in slide-in-from-right duration-300"
          data-testid="incoming-call-notification"
        >
          <Card className="w-80 shadow-2xl border-2 border-green-500 bg-white">
            <CardContent className="p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center animate-pulse">
                    <PhoneIncoming className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-green-600">Incoming Call</p>
                    <p className="text-xs text-slate-500">Ringing...</p>
                  </div>
                </div>
              </div>

              {/* Caller Info */}
              <div className="mb-4 text-center py-3 bg-slate-50 rounded-lg">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <User className="h-6 w-6 text-indigo-600" />
                </div>
                <p className="font-semibold text-slate-900">
                  {callerInfo?.name || 'Unknown Caller'}
                </p>
                {callerInfo?.company && (
                  <p className="text-sm text-slate-600">{callerInfo.company}</p>
                )}
                <p className="text-sm text-slate-500">
                  {formatPhone(callerInfo?.phone)}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  onClick={declineCall}
                  variant="destructive"
                  className="flex-1 gap-2"
                  data-testid="decline-call-btn"
                >
                  <PhoneOff className="h-4 w-4" />
                  Decline
                </Button>
                <Button 
                  onClick={answerCall}
                  className="flex-1 gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="answer-call-btn"
                >
                  <Phone className="h-4 w-4" />
                  Answer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Call Widget - Fixed position at bottom right */}
      {callState === 'active' && activeCall && (
        <div 
          className="fixed bottom-4 right-4 z-[100] animate-in slide-in-from-bottom duration-300"
          data-testid="active-call-widget"
        >
          <Card className="w-72 shadow-2xl border-2 border-green-500 bg-white">
            <CardContent className="p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <Phone className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{callerInfo?.name || 'Active Call'}</p>
                    <p className="text-xs text-green-600 font-mono">{formatDuration(callDuration)}</p>
                  </div>
                </div>
              </div>

              {/* Phone Number */}
              <p className="text-sm text-slate-500 mb-3 text-center">
                {formatPhone(callerInfo?.phone)}
              </p>

              {/* Controls */}
              <div className="flex items-center justify-center gap-2">
                <Button 
                  onClick={toggleMute}
                  variant="outline"
                  size="icon"
                  className={`h-10 w-10 rounded-full ${isMuted ? 'bg-red-100 text-red-600 border-red-200' : ''}`}
                  title={isMuted ? 'Unmute' : 'Mute'}
                  data-testid="mute-btn"
                >
                  {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
                <Button 
                  onClick={toggleDeafen}
                  variant="outline"
                  size="icon"
                  className={`h-10 w-10 rounded-full ${isDeafened ? 'bg-amber-100 text-amber-600 border-amber-200' : ''}`}
                  title={isDeafened ? 'Undeafen' : 'Deafen'}
                  data-testid="deafen-btn"
                >
                  {isDeafened ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </Button>
                <Button 
                  onClick={hangUp}
                  variant="destructive"
                  size="icon"
                  className="h-10 w-10 rounded-full"
                  title="End Call"
                  data-testid="hangup-btn"
                >
                  <PhoneOff className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
